import { memo, useMemo, useRef } from "react";
import { serializableInvariant } from "@circle-react-shared/BuilderUI/Helpers/isSerializable";
import type { BuilderUIStore } from "@circle-react-shared/BuilderUI/Store";
import { createBuilderUIStore } from "@circle-react-shared/BuilderUI/Store";
import type { BuilderUIProviderProps } from "@circle-react-shared/BuilderUI/Store/types";
import { BuilderUIContext } from "./BuilderUIProvider";
import { useClearHistoryOnPageChange } from "./useClearHistoryOnPageChange";
import { useDebouncedOnChange } from "./useDebouncedOnChange";
import { useWindowDebugOptions } from "./useWindowDebugOptions";

export const StoreProvider = memo(
  ({ children, onChange, ...initialData }: BuilderUIProviderProps) => {
    const store = useRef<BuilderUIStore>();
    const prevInitialData = useRef(initialData);

    const hasDataChanged = useMemo(
      () =>
        JSON.stringify(prevInitialData.current) !== JSON.stringify(initialData),
      [initialData],
    );

    if (!store.current || hasDataChanged) {
      serializableInvariant(initialData.pages || initialData.block);
      store.current = createBuilderUIStore(initialData);
      prevInitialData.current = initialData;
    }

    useDebouncedOnChange(store, onChange);
    useClearHistoryOnPageChange(store);
    useWindowDebugOptions(store);

    return (
      <BuilderUIContext.Provider value={store.current}>
        {children}
      </BuilderUIContext.Provider>
    );
  },
);

StoreProvider.displayName = "StoreProvider";
